import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import { ChangeEvent, KeyboardEvent, ReactNode, useRef } from "react"
import { useController } from "../../../hooks/useController"
import useKeyDown from "../../../hooks/useKeyDown"
import { endStepRun, markStep } from "../../../helpers/stepUndoGroup.helpers"
import { isStepChange, stepNumber, useWheelStep } from "../../../hooks/useNumberStep"

interface Props {
    /** the field name handed to `handleOnChange` as `e.target.name` */
    name: string
    value: number | string | undefined
    /** the field's name shown inside it, above the value — "top (px)" */
    label?: string
    /** short text or icon in front of the value, Figma style: X, Y, W, H, an angle */
    prefix?: ReactNode
    /** what the field is, shown on hover — the prefix alone can be terse */
    tooltip: string
    handleOnChange: (e: any) => void
    min?: number
    max?: number
    step?: number
    disabled?: boolean
    /** "number" unless the style takes words too (a text's width can be `auto`) */
    type?: "number" | "text"
    /** shown while the field is empty — "Mixed" when it stands for values that differ */
    placeholder?: string
    /** a small action or the unit at the end of the field */
    endAdornment?: ReactNode
    /** the value is invalid (e.g. a negative time) */
    error?: boolean
    /**
     * go back to `value` after every commit — for a field that holds a CHANGE rather than a
     * value ("move by"), so each commit, and each step, applies once
     */
    resetAfterCommit?: boolean
}

/**
 * A compact field that fills its column, labelled inside the field (`label`) or by a prefix.
 * Commits on blur and Enter, like NumberInput — and at once when a number is stepped (spin
 * arrows, ↑ / ↓, the mouse wheel), see useNumberStep.
 */
const PrefixedInput = ({
    name,
    value,
    label,
    prefix,
    tooltip,
    handleOnChange,
    min,
    max,
    step = 1,
    disabled,
    type = "number",
    placeholder,
    endAdornment,
    error,
    resetAfterCommit,
}: Props) => {
    const controller = useController(value as string)
    const { handleOnEnterKeyDown } = useKeyDown()
    const inputRef = useRef<HTMLInputElement>(null)
    const isNumber = type === "number"

    // an empty field ("Mixed") has no value to step from — a step would start at 0 and set
    // every selected object to it, so stepping only commits once there is a value
    const hasValue = String(controller.value ?? "") !== ""

    /**
     * @param stepped the commit is a step (arrows, ↑ / ↓, wheel), which joins the field's
     *        stepping run — one undo step for the run; a typed value is a step of its own
     */
    const commit = (e: any, stepped = false) => {
        if (stepped) markStep(inputRef.current)
        else endStepRun()
        /**
         * Typed below the minimum (a negative width): nothing is written and the field goes
         * back to the last valid value. Stepping already stops at the minimum.
         */
        const typed = String(e?.target?.value ?? "")
        if (isNumber && min !== undefined && typed.trim() !== "" && Number(typed) < min) {
            controller.onChange({
                target: { value: String(value ?? "") },
            } as ChangeEvent<HTMLInputElement>)
            return
        }
        handleOnChange(e)
        if (resetAfterCommit)
            controller.onChange({
                target: { value: String(value ?? "") },
            } as ChangeEvent<HTMLInputElement>)
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        controller.onChange(e)
        if (isNumber && hasValue && isStepChange(e)) commit(e, true)
    }

    useWheelStep(
        inputRef,
        (direction) => {
            if (!hasValue) return
            const next = String(stepNumber(Number(controller.value), direction, step, min, max))
            controller.onChange({ target: { value: next } } as ChangeEvent<HTMLInputElement>)
            commit({ target: { name, value: next } }, true)
        },
        isNumber && !disabled
    )

    return (
        <Tooltip title={tooltip} placement="top" enterDelay={600}>
            <TextField
                type={type}
                name={name}
                value={controller.value ?? ""}
                onChange={handleChange}
                inputRef={inputRef}
                onBlur={(e) => commit(e)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                    handleOnEnterKeyDown(e, (event: any) => commit(event))
                }
                disabled={disabled}
                placeholder={placeholder}
                error={error}
                label={label}
                hiddenLabel={!label}
                InputLabelProps={label ? { shrink: true } : undefined}
                fullWidth
                sx={{ m: 0 }}
                InputProps={{
                    startAdornment: prefix !== undefined && (
                        <InputAdornment
                            position="start"
                            // never squeezed below its text, or it would run over the value
                            sx={{ color: "text.secondary", minWidth: 14, mr: 0.5, flexShrink: 0 }}
                        >
                            {prefix}
                        </InputAdornment>
                    ),
                    endAdornment: endAdornment && (
                        <InputAdornment position="end">{endAdornment}</InputAdornment>
                    ),
                }}
                inputProps={{ min, max, step, "aria-label": tooltip }}
            />
        </Tooltip>
    )
}

export default PrefixedInput
